import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { useGetEquityFilingsApiV1EquitySymbolFilingsGet } from '#/api/generated/equity/equity';
import type {
  Filing,
  Filings,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { DataTable } from '#/components/data/DataTable';
import { TableSectionSkeleton } from '#/components/detail/DetailSkeleton';
import { NoData } from '#/components/ui/NoData';
import { QueryBoundary } from '#/components/ui/QueryBoundary';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { SkeletonRegion } from '#/components/ui/Skeleton';
import { Tabs } from '#/components/ui/Tabs';
import { DASH } from '#/utils/constants';
import { formatMonthDayYearDate } from '#/utils/format';

type FilingCategoryId = keyof Filings;

const FILING_TABS: { id: FilingCategoryId; label: string }[] = [
  { id: 'financials', label: 'Financials' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'ownership', label: 'Ownership' },
  { id: 'offering_statements', label: 'Offering Statements' },
  { id: 'insider_activities', label: 'Insider Activities' },
  { id: 'other', label: 'Other' },
];

interface EquityFilingsSectionProps {
  symbol: string;
  intro?: string;
}

function filingColumns(categoryLabel: string): ColumnDef<Filing>[] {
  return [
    {
      id: 'title',
      accessorKey: 'title',
      header: categoryLabel,
      enableSorting: false,
      cell: ({ row }) => {
        const title = row.original.title ?? DASH;

        if (!row.original.url) {
          return title;
        }

        return (
          <a
            className="underline underline-offset-2"
            href={row.original.url}
            target="_blank"
            rel="noopener noreferrer"
            data-track="detail_filing_link"
            data-track-label={row.original.title ?? undefined}
            data-track-context="filings"
          >
            {title}
          </a>
        );
      },
    },
    {
      id: 'language',
      header: 'Language',
      enableSorting: false,
      cell: () => 'English',
    },
    {
      accessorKey: 'form_type',
      header: 'Form Type',
      enableSorting: false,
      cell: ({ getValue }) => getValue<string | null>() ?? <NoData />,
    },
    {
      accessorKey: 'effective_date',
      header: 'Effective Date',
      enableSorting: false,
      cell: ({ getValue }) =>
        formatMonthDayYearDate(getValue<string | null>()) ?? <NoData />,
    },
    {
      accessorKey: 'post_date',
      header: 'Post Date',
      enableSorting: false,
      cell: ({ getValue }) =>
        formatMonthDayYearDate(getValue<string | null>()) ?? <NoData />,
    },
  ];
}

function filingRowId(row: Filing, index: number) {
  return `${row.form_type ?? 'filing'}-${row.post_date ?? 'date'}-${row.url ?? index}`;
}

export function EquityFilingsSection({
  symbol,
  intro,
}: EquityFilingsSectionProps) {
  const [activeTab, setActiveTab] = useState<FilingCategoryId>('financials');
  const query = useGetEquityFilingsApiV1EquitySymbolFilingsGet(symbol);
  const activeTabLabel =
    FILING_TABS.find((tab) => tab.id === activeTab)?.label ?? 'Filings';
  const columns = useMemo(
    () => filingColumns(activeTabLabel),
    [activeTabLabel],
  );

  return (
    <QueryBoundary
      query={query}
      // Holds `#filings` in the DOM while this section's own request is in
      // flight, so the nav does not advertise Filings, drop it, then re-add it.
      loading={
        <SkeletonRegion label={`Loading filings for ${symbol}…`}>
          <TableSectionSkeleton id="filings" rows={11} />
        </SkeletonRegion>
      }
      notFound={() => null}
      renderError={() => null}
    >
      {(response) => {
        const hasFilings = FILING_TABS.some(
          (tab) => (response.data[tab.id]?.length ?? 0) > 0,
        );

        if (!hasFilings) {
          return null;
        }

        const filings = response.data[activeTab] ?? [];

        return (
          <section
            id="filings"
            className="flow flow-inner-8 scroll-mt-(--scroll-offset)"
          >
            <SectionHeader
              title="Filings"
              actionAlignment="center"
              description={intro}
            />

            <div className="flow-32 p-gap-inset border-divider-subtle -mx-gap border-t border-b sm:mx-0 sm:rounded sm:border">
              <Tabs
                aria-label="Filing categories"
                tabs={FILING_TABS}
                activeTab={activeTab}
                onChange={(id) => setActiveTab(id)}
              />
              <div
                id={`filings-panel-${activeTab}`}
                role="tabpanel"
                aria-labelledby={activeTab}
                className="mt-32"
              >
                <DataTable
                  key={activeTab}
                  aria-label={`${activeTabLabel} filings`}
                  columns={columns}
                  data={filings}
                  getRowId={filingRowId}
                  pageSize={11}
                  emptyState={`No ${activeTabLabel.toLowerCase()} filings on file`}
                />
              </div>
            </div>
          </section>
        );
      }}
    </QueryBoundary>
  );
}
